import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Icon, Intent } from '@blueprintjs/core';
import moment from 'moment';
import Toast from 'helpers/toaster';
import PDFViewerPurchaseOrder from 'components/WorkReport/PDFViewerPurchaseOrder';
import { MATERIAL_STATUSES, ARCHIVED, ALL_MATERIAL_STATUSES, materialStatus } from 'helpers/materialStatus';

const failureReason = (error, fallback) => {
  const data = error && error.response && error.response.data;
  const reason = data && (data.errors || data.error);
  return reason ? (Array.isArray(reason) ? reason.join(', ') : reason) : fallback;
};

// A purchase order (pages/work-reports/show-purchase-order.js), shown as the
// document the supplier gets: a centred A4 sheet with the letterhead, who it's
// to, the order details and the materials, stamped with where it's at. The
// controls (status, archive, PDF) sit in a white strip above the sheet, so
// the sheet itself stays clean.
@inject('workReportStore') @observer
class PurchaseOrderView extends Component {
  @observable downloadingPdf = false;
  @observable savingMaterial = null; // id of the material whose status is being saved

  get order() {
    return this.props.workReportStore.purchaseOrderItem.object;
  }

  setStatus(status) {
    const { workReportStore } = this.props;
    if (workReportStore.isSavingPurchaseOrder || status === this.order.attributes.status) return;
    const label = materialStatus(status).label;
    workReportStore.setPurchaseOrderStatus(this.order.id, status)
      .then(() => {
        Toast.show({ message: status === ARCHIVED.value ? 'Purchase order archived.' : `Marked as ${label}.`, intent: Intent.SUCCESS });
      })
      .catch((error) => {
        Toast.show({ message: failureReason(error, "Couldn't change the status. Try again."), intent: Intent.DANGER });
      });
  }

  setMaterialStatus(material, status) {
    if (status === material.status) return;
    this.savingMaterial = material.id;
    this.props.workReportStore.setMaterialStatus(material, status)
      .then(() => {
        Toast.show({ message: `${material.description || 'Material'} marked as ${materialStatus(status).label}.`, intent: Intent.SUCCESS });
      })
      .catch((error) => {
        Toast.show({ message: failureReason(error, "Couldn't change the material's status. Try again."), intent: Intent.DANGER });
      })
      .finally(() => { this.savingMaterial = null; });
  }

  downloadPdf = () => {
    this.downloadingPdf = true;
    this.props.workReportStore.downloadPurchaseOrderPDF()
      .catch(() => {
        Toast.show({ message: "Couldn't download the PDF. Try again.", intent: Intent.DANGER });
      })
      .finally(() => { this.downloadingPdf = false; });
  };

  // The strip above the sheet: the order's status as a dropdown on the left,
  // the actions on the right.
  renderToolbar() {
    const { workReportStore } = this.props;
    const a = this.order.attributes;
    const saving = workReportStore.isSavingPurchaseOrder;
    const archived = a.status === ARCHIVED.value;
    const known = MATERIAL_STATUSES.some((status) => status.value === a.status);
    const count = (a.material_line_items || []).length;
    let hint = null;
    if (archived) hint = 'Archived. Pick a status to bring it back.';
    else if (count > 0) hint = `Changing it moves ${count === 1 ? 'its material' : `all ${count} materials`} too.`;
    return (
      <div className="pov-toolbar">
        <div className="pov-toolbar__status">
          <label className="pov-toolbar__label" htmlFor="pov-order-status">Status</label>
          <select
            id="pov-order-status"
            className={`pov-status pov-status--${materialStatus(a.status).tone} pov-status--order`}
            value={known || archived ? a.status : ''}
            disabled={saving}
            title={hint || undefined}
            aria-describedby={hint ? 'pov-order-status-hint' : undefined}
            onChange={(event) => this.setStatus(event.target.value)}
          >
            {!known && !archived && <option value="" disabled>Choose a status</option>}
            {archived && <option value={ARCHIVED.value} disabled>{ARCHIVED.label}</option>}
            {MATERIAL_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          {/* Said on hover and to screen readers; the strip stays on one line. */}
          {hint && <span className="pov-toolbar__hint sr-only" id="pov-order-status-hint">{hint}</span>}
        </div>
        <div className="pov-toolbar__actions">
          {!archived && (
            <button type="button" className="pov-tool" disabled={saving} onClick={() => this.setStatus(ARCHIVED.value)}>
              <Icon icon="archive" iconSize={14} /> Archive order
            </button>
          )}
          <button type="button" className="pov-tool" onClick={() => { workReportStore.showPdfViewer = true; }}>
            <Icon icon="eye-open" iconSize={14} /> View PDF
          </button>
          <button type="button" className="pov-tool pov-tool--primary" disabled={this.downloadingPdf} aria-busy={this.downloadingPdf} onClick={this.downloadPdf}>
            <Icon icon="download" iconSize={14} /> {this.downloadingPdf ? 'Downloading…' : 'Download PDF'}
          </button>
        </div>
      </div>
    );
  }

  // The status, stamped on the sheet the way paper orders get marked.
  renderStamp() {
    const { status } = this.order.attributes;
    if (!status) return null;
    const { label, tone } = materialStatus(status);
    return <div className={`pov-stamp pov-stamp--${tone}`} role="img" aria-label={`Status: ${label}`}>{label}</div>;
  }

  renderLetterhead() {
    const tenant = this.order.attributes.tenant || {};
    const place = [tenant.city, tenant.province, tenant.postal_code].filter(Boolean).join(', ');
    return (
      <header className="pov-letterhead">
        <div className="pov-letterhead__logo">
          {tenant.logo ? <img src={tenant.logo} alt={tenant.name || 'Company logo'} /> : <span className="pov-letterhead__name">{tenant.name}</span>}
        </div>
        <address className="pov-letterhead__address">
          {tenant.logo && tenant.name && <strong>{tenant.name}</strong>}
          {tenant.street_address && <span>{tenant.street_address}</span>}
          {place && <span>{place}</span>}
          {tenant.phone && <span>{tenant.phone}</span>}
        </address>
      </header>
    );
  }

  renderParties() {
    const a = this.order.attributes;
    const supplier = a.supplier || {};
    const items = a.material_line_items || [];
    const units = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    return (
      <div className="pov-parties">
        <section className="pov-party" aria-labelledby="pov-to">
          <h2 className="pov-label" id="pov-to">To</h2>
          {supplier.name && (
            <a className="pov-party__name" href={supplier.id ? `/dashboard/suppliers/${supplier.id}/show` : undefined}>{supplier.name}</a>
          )}
          {supplier.address && <span>{supplier.address}</span>}
          {supplier.phone && <a href={`tel:${supplier.phone}`}>{supplier.phone}</a>}
          {supplier.email && <a href={`mailto:${supplier.email}`}>{supplier.email}</a>}
        </section>
        <dl className="pov-facts">
          <div><dt>Order number</dt><dd>{a.purchase_order_number}</dd></div>
          {a.created_at && <div><dt>Date</dt><dd>{moment(a.created_at).format('MMM D, YYYY')}</dd></div>}
          <div><dt>Materials</dt><dd>{items.length} {items.length === 1 ? 'line' : 'lines'}, {units} {units === 1 ? 'unit' : 'units'}</dd></div>
        </dl>
      </div>
    );
  }

  renderMaterials() {
    const items = this.order.attributes.material_line_items || [];
    if (items.length === 0) {
      return (
        <div className="pov-empty">
          <strong>No materials on this order</strong>
          <span>This purchase order has no materials.</span>
        </div>
      );
    }
    return (
      <table className="pov-table">
        <thead>
          <tr>
            <th scope="col">Material</th>
            <th scope="col">Part no.</th>
            <th scope="col" className="pov-num">Qty</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((material) => {
            const status = materialStatus(material.status);
            const quantity = parseFloat(material.quantity) || 0;
            return (
              <tr key={material.id}>
                <td className="pov-table__item">
                  <span>{material.description || 'Material'}</span>
                  {material.work_report_id && (
                    <a className="pov-wo" href={`/dashboard/work-reports/${material.work_report_id}/show`} aria-label="Open its work order" title="Open its work order">
                      <Icon icon="clipboard" iconSize={12} />
                    </a>
                  )}
                </td>
                <td className="pov-table__part">{material.part_number || <span className="pov-faint">None</span>}</td>
                <td className="pov-num">{quantity}</td>
                <td>
                  <select
                    className={`pov-status pov-status--${status.tone}`}
                    aria-label={`Status of ${material.description || 'material'}`}
                    value={material.status || ''}
                    disabled={this.savingMaterial === material.id}
                    onChange={(event) => this.setMaterialStatus(material, event.target.value)}
                  >
                    {!material.status && <option value="" disabled>Choose a status</option>}
                    {ALL_MATERIAL_STATUSES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  render() {
    const a = this.order.attributes;
    return (
      <div className="pov">
        {this.renderToolbar()}
        <article className="pov-sheet" aria-labelledby="pov-title">
          {this.renderLetterhead()}
          <div className="pov-title-row">
            <h1 className="pov-title" id="pov-title">
              Purchase order <span className="pov-title__number">{a.purchase_order_number}</span>
            </h1>
            {this.renderStamp()}
          </div>
          {this.renderParties()}
          {this.renderMaterials()}
        </article>
        <PDFViewerPurchaseOrder />
      </div>
    );
  }
}

// Shown while the order loads: the sheet's outline, shimmering.
export const PurchaseOrderSkeleton = () => (
  <div className="pov cp--loading" role="status" aria-label="Loading purchase order">
    <div className="pov-toolbar" aria-hidden="true">
      <span className="dl-bone" style={{ width: 220, height: 28 }} />
      <span className="dl-bone" style={{ width: 300, height: 32, borderRadius: 8 }} />
    </div>
    <div className="pov-sheet" aria-hidden="true">
      <div className="pov-letterhead">
        <span className="dl-bone" style={{ width: 120, height: 48 }} />
        <span className="dl-bone" style={{ width: 180, height: 56 }} />
      </div>
      <span className="dl-bone" style={{ width: 320, height: 32 }} />
      <span className="dl-bone" style={{ width: '100%', height: 96, marginTop: 28 }} />
      <span className="dl-bone" style={{ width: '100%', height: 160, marginTop: 28 }} />
    </div>
  </div>
);

export default PurchaseOrderView;
